import { useEffect, useState } from 'react';
import { useUserState } from '../../../hooks/context/useUserState';
import { requestReportTemplate } from '../../../api/requests/reportTemplates/requestReportTemplates';
import { ReportTemplate } from '../types';
import { isReportTemplateLayout } from '../utils/validateReportTemplate';

export type StoredTemplateState = {
  template: ReportTemplate | undefined;
  isLoading: boolean;
  /** The id was not the generated default and could not be loaded. */
  failed: boolean;
};

const IDLE: StoredTemplateState = {
  template: undefined,
  isLoading: false,
  failed: false,
};

/**
 * Loads a stored report template.
 *
 * Pass an id only when one genuinely has to be fetched — the caller decides
 * that, because it needs the dataset to tell a stored id from the generated
 * default. Nothing to fetch means the idle state, never a loading one: a page
 * that reports itself as loading while no request is in flight has no event
 * left that would clear it.
 *
 * A failure is reported rather than thrown: the print page falls back to the
 * generated default and says so, because refusing to print anything would be a
 * worse answer than printing the default.
 */
export const useStoredReportTemplate = (
  templateId: string | undefined,
  expectedDatasetId: string | undefined
): StoredTemplateState => {
  const {
    state: { token },
  } = useUserState();
  const [state, setState] = useState<StoredTemplateState>(IDLE);

  useEffect(() => {
    if (!templateId) {
      setState((previous) => (previous === IDLE ? previous : IDLE));
      return;
    }

    let cancelled = false;
    setState({ template: undefined, isLoading: true, failed: false });

    requestReportTemplate(templateId, token)
      .then((detail) => {
        if (cancelled) return;
        if (
          !expectedDatasetId ||
          detail.isActive !== true ||
          detail.datasetId !== expectedDatasetId ||
          !isReportTemplateLayout(detail.layout) ||
          detail.layout.datasetId !== expectedDatasetId
        ) {
          throw new Error('Invalid stored report template layout');
        }
        // The row name is the canonical user-visible identity. Older clients
        // could persist a different layout.label (notably with surrounding
        // whitespace), which otherwise made the menu and reportTitle disagree.
        const canonicalName =
          typeof detail.name === 'string' && detail.name.trim().length > 0
            ? detail.name.trim()
            : detail.layout.label;
        setState({
          template: { ...detail.layout, label: canonicalName },
          isLoading: false,
          failed: false,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ template: undefined, isLoading: false, failed: true });
      });

    return () => {
      cancelled = true;
    };
  }, [templateId, expectedDatasetId, token]);

  return state;
};
